import {
  PopulatedCatalogProductWithVariants,
  PopulatedVariant,
} from "@redotech/marketplace-sdk/rpc/schema/get-product-details";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { ImageDisplayWithPlaceholder } from "@redotech/redo-web/image-display-with-placeholder";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useState } from "react";
import { useCart, useCartUI } from "../../context";
import * as productDetailsStyles from "./product-details.module.css";

export interface ProductDetailsProps {
  product: PopulatedCatalogProductWithVariants;
}

export const ProductDetails = memo(function ProductDetails({
  product,
}: ProductDetailsProps) {
  const { addItem, getItem, updateQuantity } = useCart();
  const { openDrawer } = useCartUI();
  const [selectedVariant, setSelectedVariant] = useState<PopulatedVariant>(
    product.variants[0] || ({} as PopulatedVariant),
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const cartItem = selectedVariant
    ? getItem(product._id, selectedVariant._id)
    : undefined;
  const isInCart = !!cartItem;

  // Get available inventory for selected variant from the variant data itself
  const availableQuantity = selectedVariant?.available || 0;
  const currentCartQuantity = cartItem?.quantity || 0;
  const remainingQuantity = Math.max(
    0,
    availableQuantity - currentCartQuantity,
  );

  const handleVariantChange = useCallback(
    (variant: PopulatedVariant | undefined) => {
      if (variant) {
        setSelectedVariant(variant);
        // Reset quantity when variant changes, but don't exceed available inventory
        const variantAvailableQuantity = variant.available || 0;
        const variantCartItem = getItem(product._id, variant._id);
        const variantRemainingQuantity = Math.max(
          0,
          variantAvailableQuantity - (variantCartItem?.quantity || 0),
        );
        setQuantity(Math.min(1, variantRemainingQuantity));
      }
    },
    [getItem, product._id],
  );

  const handleQuantityChange = useCallback(
    (value: string) => {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && numValue >= 1 && numValue <= remainingQuantity) {
        setQuantity(numValue);
      }
    },
    [remainingQuantity],
  );

  const incrementQuantity = useCallback(() => {
    setQuantity((prev) => Math.min(prev + 1, remainingQuantity));
  }, [remainingQuantity]);

  const decrementQuantity = useCallback(() => {
    setQuantity((prev) => Math.max(1, prev - 1));
  }, []);

  const handleAddToCart = useCallback(async () => {
    if (selectedVariant) {
      openDrawer();

      if (isInCart) {
        // Update existing cart item quantity
        await updateQuantity(
          product._id,
          selectedVariant._id,
          cartItem!.quantity + quantity,
        );
      } else {
        // Add new item to cart
        const cartProduct = {
          ...product,
          attributes: [],
          variants: product.variants.map((v) => ({
            ...v,
            attributeValues: v.optionValues,
            components: [],
          })),
        };
        const cartVariant = {
          ...selectedVariant,
          attributeValues: selectedVariant.optionValues,
          components: [],
        };
        await addItem(cartProduct, cartVariant, quantity);
      }
    }
  }, [
    selectedVariant,
    quantity,
    isInCart,
    cartItem,
    updateQuantity,
    addItem,
    product,
    openDrawer,
  ]);

  const featuredImage = product.images[selectedImageIndex] || product.images[0];
  const hasVariants = product.variants.length > 0;
  const hasMultipleVariants = product.variants.length > 1;

  const handleImageClick = useCallback((index: number) => {
    setSelectedImageIndex(index);
  }, []);

  // Helper function to format variant display name from option values
  const getVariantDisplayName = useCallback(
    (variant: PopulatedVariant) => {
      if (!variant.optionValues || variant.optionValues.length === 0) {
        return `Variant ${product.variants.indexOf(variant) + 1}`;
      }

      // Join option values with ' / '
      const displayName = variant.optionValues.filter(Boolean).join(" / ");
      return displayName;
    },
    [product.variants],
  );

  return (
    <Flex dir="column" gap="lg" w="full">
      <div className={productDetailsStyles.productGrid}>
        {/* Image Section */}
        <div className={productDetailsStyles.imageSection}>
          <div className={productDetailsStyles.productImageContainer}>
            <ImageDisplayWithPlaceholder
              alt={featuredImage?.altText || product.name}
              className={productDetailsStyles.productImage}
              size="lg"
              src={featuredImage?.url || ""}
            />
          </div>

          {product.images.length > 1 && (
            <div className={productDetailsStyles.thumbnailContainer}>
              {product.images.slice(0, 5).map((image, index) => (
                <div
                  className={`${productDetailsStyles.thumbnailItem} ${
                    index === selectedImageIndex
                      ? productDetailsStyles.selectedThumbnail
                      : ""
                  }`}
                  key={image._id}
                  onClick={() => handleImageClick(index)}
                  style={{ cursor: "pointer" }}
                >
                  <ImageDisplayWithPlaceholder
                    alt={image.altText || product.name}
                    className={productDetailsStyles.thumbnailImage}
                    size="sm"
                    src={image.url}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={productDetailsStyles.contentSection}>
          <div className={productDetailsStyles.productInfo}>
            <div>
              <Text
                color="black"
                style={{
                  fontFamily: "Baskerville, serif",
                  transform: "scaleY(1.2)",
                  fontSize: "3rem",
                }}
              >
                {product.name}
              </Text>

              <Text
                fontSize="md"
                textColor={availableQuantity > 0 ? "secondary" : "error"}
              >
                {availableQuantity > 0
                  ? `${availableQuantity} available`
                  : "Out of stock"}
              </Text>
            </div>

            {hasMultipleVariants && (
              <div className="space-y-2">
                <Text fontSize="sm" fontWeight="semibold">
                  Select Variant:
                </Text>
                <SelectDropdown
                  className={productDetailsStyles.variantSelect}
                  options={product.variants}
                  value={selectedVariant}
                  valueChange={handleVariantChange}
                >
                  {(variant) => (
                    <Flex align="center" justify="space-between" w="full">
                      <Text>{getVariantDisplayName(variant)}</Text>
                      {variant.msrp &&
                        variant.price &&
                        variant.price < variant.msrp && (
                          <div className="flex items-baseline gap-2">
                            <Text fontWeight="bold">
                              ${variant.price.toFixed(2)}
                            </Text>
                            {variant.msrp && variant.msrp > variant.price && (
                              <>
                                <Text className="text-gray-400 text-xs">
                                  Compare at
                                </Text>
                                <Text className="text-gray-400 text-sm line-through">
                                  ${variant.msrp.toFixed(2)}
                                </Text>
                              </>
                            )}
                          </div>
                        )}
                    </Flex>
                  )}
                </SelectDropdown>
              </div>
            )}

            {!hasMultipleVariants && (
              <div>
                <Text fontSize="md" textColor="secondary">
                  {/* @ts-expect-error TODO: noUncheckedIndexedAccess FIXME */}
                  {getVariantDisplayName(product.variants[0])}
                </Text>
              </div>
            )}

            {selectedVariant?.price && (
              <div>
                <Text fontSize="md" textColor="secondary">
                  Buy now for
                </Text>
                <div className="flex items-baseline gap-3">
                  <Text
                    color="black"
                    fontWeight="semibold"
                    style={{ fontSize: "30px" }}
                  >
                    ${selectedVariant.price.toFixed(2)}
                  </Text>
                  {selectedVariant.msrp
                    ? selectedVariant.msrp > selectedVariant.price && (
                        <div className="flex items-baseline gap-1.5">
                          <Text className="text-gray-500 text-xs font-medium">
                            Compare at
                          </Text>
                          <Text className="text-gray-500 text-lg line-through font-normal">
                            ${selectedVariant.msrp.toFixed(2)}
                          </Text>
                        </div>
                      )
                    : null}
                </div>

                {isInCart && (
                  <Text color="gray" fontSize="sm">
                    Currently in cart: {cartItem!.quantity}
                  </Text>
                )}
              </div>
            )}
          </div>

          {/* Purchase Controls - Pinned to Bottom */}
          <div className={productDetailsStyles.purchaseControls}>
            <Flex dir="column" gap="md">
              <div>
                <Text fontSize="md" textColor="secondary">
                  Quantity
                </Text>
                <Flex align="center" gap="sm" style={{ marginTop: "8px" }}>
                  <RedoButton
                    disabled={quantity <= 1}
                    hierarchy="secondary"
                    onClick={decrementQuantity}
                    size="sm"
                    text="-"
                  />
                  <RedoTextInput
                    className={productDetailsStyles.quantityInput}
                    setValue={handleQuantityChange}
                    size="sm"
                    value={quantity.toString()}
                  />
                  <RedoButton
                    disabled={quantity >= remainingQuantity}
                    hierarchy="secondary"
                    onClick={incrementQuantity}
                    size="sm"
                    text="+"
                  />
                </Flex>
              </div>

              <RedoButton
                className="w-full max-w-[200px]"
                disabled={!hasVariants || remainingQuantity === 0}
                hierarchy="primary"
                onClick={handleAddToCart}
                size="md"
                text={
                  remainingQuantity === 0
                    ? "Out of Stock"
                    : isInCart
                      ? `Add ${quantity} More to Cart`
                      : `Add ${quantity} to Cart`
                }
              />
            </Flex>
          </div>
        </div>
      </div>
    </Flex>
  );
});
